import { useEffect, useRef, useState } from "react";

const ARROW =
  "M 3 1 L 3 19 L 8 14 L 11 23 L 14.5 21.5 L 11 12.5 L 17 12.5 Z";

export default function CustomCursor() {
  const wrapRef   = useRef(null);
  const trailRef  = useRef(null);
  const targetRef = useRef({ x: -200, y: -200 });
  const posRef      = useRef({ x: -200, y: -200 });
  const [mode, setMode]       = useState("default");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth < 1024) return;

    const onMove = (e) => {
      targetRef.current = { x: e.clientX, y: e.clientY };
      setVisible(true);
      if (wrapRef.current) {
        wrapRef.current.style.transform =
          `translate3d(${e.clientX - 3}px, ${e.clientY - 1}px, 0)`;
      }
    };

    const onOver = (e) => {
      const t = e.target;
      if (!t?.closest) return;
      if (t.closest("[data-cursor='view']"))
        setMode("view");
      else if (t.closest("a, button, [role='button'], input, textarea, select"))
        setMode("link");
      else
        setMode("default");
    };

    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    let raf;
    const loop = () => {
      const k = 0.10;
      posRef.current.x += (targetRef.current.x - posRef.current.x) * k;
      posRef.current.y += (targetRef.current.y - posRef.current.y) * k;
      const tx = posRef.current.x - 22;
      const ty = posRef.current.y - 22;
      if (trailRef.current) {
        trailRef.current.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      cancelAnimationFrame(raf);
    };
  }, []);

  const scale = mode === "view" ? 1.25 : mode === "link" ? 1.15 : 1;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[9999] hidden lg:block"
      style={{ opacity: visible ? 1 : 0, transition: "opacity 200ms ease" }}
      data-testid="custom-cursor"
    >
      {/* Lagging glow blob — reacts to mode */}
      <div
        ref={trailRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 44,
          height: 44,
          borderRadius: "50%",
          background:
            mode === "link"
              ? "rgba(124,196,216,0.24)"
              : "rgba(124,196,216,0.12)",
          filter: "blur(16px)",
          willChange: "transform",
          transition: "background 250ms ease",
        }}
      />

      {/* Cursor SVG — exact position, no lag */}
      <div
        ref={wrapRef}
        style={{ position: "fixed", top: 0, left: 0, willChange: "transform" }}
      >
        <div
          style={{
            transformOrigin: "3px 1px",
            transform: `scale(${scale})`,
            transition: "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
            position: "relative",
          }}
        >
          <svg
            width="28"
            height="32"
            viewBox="-4 -3 28 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient
                id="ng"
                x1="3" y1="1"
                x2="13" y2="24"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%"   stopColor="#F2E8D8" />
                <stop offset="45%"  stopColor="#7CC4D8" />
                <stop offset="100%" stopColor="#4A9CB5" />
              </linearGradient>

              <filter id="ng-glow" x="-80%" y="-80%" width="260%" height="260%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="4" result="b1" />
                <feFlood floodColor="#7CC4D8" floodOpacity="0.55" result="c1" />
                <feComposite in="c1" in2="b1" operator="in" result="glow1" />
                <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" result="b2" />
                <feFlood floodColor="#F2E8D8" floodOpacity="0.90" result="c2" />
                <feComposite in="c2" in2="b2" operator="in" result="glow2" />
                <feMerge>
                  <feMergeNode in="glow1" />
                  <feMergeNode in="glow2" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <path d={ARROW} fill="url(#ng)" filter="url(#ng-glow)" />
            <path
              d="M 3 1 L 3 19"
              stroke="rgba(242,232,216,0.55)"
              strokeWidth="0.9"
              strokeLinecap="round"
            />
          </svg>

          {mode === "view" && (
            <div
              style={{
                position: "absolute",
                top: 8,
                left: 22,
                fontFamily: '"Outfit", sans-serif',
                fontSize: 9,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                color: "#F2E8D8",
                whiteSpace: "nowrap",
                textShadow: "0 0 10px rgba(200,121,65,0.85)",
              }}
            >
              VIEW
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
